import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { RCardGet } from '../../../../../../../apiRPC/card';
import { cardAdditionalFieldsUpdate } from '../../../../../../../apiRPC/cardAdditionalFields';
import { isUuidString } from '../../../../../../../helper/methods/string';

export const GRID_API_CAMPAIGN_LABEL = 'ID РК в GRID-API';

const validateGridApiCampaignValue = (value: string): string | undefined => {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  if (!isUuidString(trimmed)) {
    return 'Укажите корректный UUID кампании (формат xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)';
  }
  return undefined;
};

const serializeGridApiCampaignForSave = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed || null;
};

type UseGridApiCampaignFieldResult = {
  gridApiCampaign: string;
  gridApiCampaignError: string | undefined;
  isGridApiCampaignDisabled: boolean;
  gridApiCampaignHelperText: string | undefined;
  handleGridApiCampaignChange: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  handleGridApiCampaignBlur: () => Promise<void>;
  handleGridApiCampaignKeyDown: (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => Promise<void>;
};

export function useGridApiCampaignField(
  card: RCardGet,
): UseGridApiCampaignFieldResult {
  const [gridApiCampaign, setGridApiCampaign] = useState(
    () => card.additionalFields?.campaign ?? '',
  );
  const [additionalFieldsId, setAdditionalFieldsId] = useState<number | null>(
    () => card.additionalFields?.id ?? null,
  );
  const [gridApiCampaignError, setGridApiCampaignError] = useState<
    string | undefined
  >();

  const gridApiCampaignSaveTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const lastSavedGridApiCampaignRef = useRef<string | null>(
    card.additionalFields?.campaign ?? null,
  );
  const cardUuidRef = useRef(card.uuid);
  const additionalFieldsIdRef = useRef<number | null>(
    card.additionalFields?.id ?? null,
  );

  useEffect(() => {
    if (gridApiCampaignSaveTimeoutRef.current) {
      clearTimeout(gridApiCampaignSaveTimeoutRef.current);
      gridApiCampaignSaveTimeoutRef.current = null;
    }

    cardUuidRef.current = card.uuid;
    additionalFieldsIdRef.current = card.additionalFields?.id ?? null;

    const campaign = card.additionalFields?.campaign ?? '';
    setGridApiCampaign(campaign);
    setAdditionalFieldsId(card.additionalFields?.id ?? null);
    setGridApiCampaignError(undefined);
    lastSavedGridApiCampaignRef.current =
      card.additionalFields?.campaign ?? null;
  }, [card.uuid, card.additionalFields?.id, card.additionalFields?.campaign]);

  const saveGridApiCampaign = useCallback(async (value: string) => {
    const saveCardUuid = cardUuidRef.current;
    const saveAdditionalFieldsId = additionalFieldsIdRef.current;

    if (saveAdditionalFieldsId === null) {
      return;
    }

    const validationError = validateGridApiCampaignValue(value);
    if (validationError) {
      if (cardUuidRef.current === saveCardUuid) {
        setGridApiCampaignError(validationError);
      }
      return;
    }

    const campaign = serializeGridApiCampaignForSave(value);
    if (campaign === lastSavedGridApiCampaignRef.current) {
      if (cardUuidRef.current === saveCardUuid) {
        setGridApiCampaignError(undefined);
      }
      return;
    }

    try {
      const result = await cardAdditionalFieldsUpdate({
        id: saveAdditionalFieldsId,
        fields: { campaign },
      });

      if (cardUuidRef.current !== saveCardUuid) {
        return;
      }

      const savedCampaign = result.campaign ?? '';
      setGridApiCampaign(savedCampaign);
      setGridApiCampaignError(undefined);
      lastSavedGridApiCampaignRef.current = result.campaign;
    } catch (error) {
      if (cardUuidRef.current !== saveCardUuid) {
        return;
      }

      console.error(error);
      setGridApiCampaignError('Ошибка при сохранении поля');
      toast.error('Ошибка при сохранении поля', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
      });
    }
  }, []);

  const debouncedSaveGridApiCampaign = useCallback(
    (value: string) => {
      const scheduleCardUuid = cardUuidRef.current;

      if (gridApiCampaignSaveTimeoutRef.current) {
        clearTimeout(gridApiCampaignSaveTimeoutRef.current);
      }

      gridApiCampaignSaveTimeoutRef.current = setTimeout(() => {
        gridApiCampaignSaveTimeoutRef.current = null;
        if (cardUuidRef.current !== scheduleCardUuid) {
          return;
        }
        saveGridApiCampaign(value);
      }, 1000);
    },
    [saveGridApiCampaign],
  );

  const handleGridApiCampaignChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newValue = event.target.value;
    setGridApiCampaign(newValue);
    const validationError = validateGridApiCampaignValue(newValue);
    setGridApiCampaignError(validationError);
    if (validationError) {
      if (gridApiCampaignSaveTimeoutRef.current) {
        clearTimeout(gridApiCampaignSaveTimeoutRef.current);
        gridApiCampaignSaveTimeoutRef.current = null;
      }
      return;
    }
    debouncedSaveGridApiCampaign(newValue);
  };

  const handleGridApiCampaignBlur = async () => {
    if (gridApiCampaignSaveTimeoutRef.current) {
      clearTimeout(gridApiCampaignSaveTimeoutRef.current);
      gridApiCampaignSaveTimeoutRef.current = null;
    }
    await saveGridApiCampaign(gridApiCampaign);
  };

  const handleGridApiCampaignKeyDown = async (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    if (gridApiCampaignSaveTimeoutRef.current) {
      clearTimeout(gridApiCampaignSaveTimeoutRef.current);
      gridApiCampaignSaveTimeoutRef.current = null;
    }
    await saveGridApiCampaign(gridApiCampaign);
  };

  useEffect(
    () => () => {
      if (gridApiCampaignSaveTimeoutRef.current) {
        clearTimeout(gridApiCampaignSaveTimeoutRef.current);
      }
    },
    [],
  );

  const isGridApiCampaignDisabled = additionalFieldsId === null;
  const gridApiCampaignHelperText = isGridApiCampaignDisabled
    ? 'Запись дополнительных полей карточки ещё не создана'
    : gridApiCampaignError;

  return {
    gridApiCampaign,
    gridApiCampaignError,
    isGridApiCampaignDisabled,
    gridApiCampaignHelperText,
    handleGridApiCampaignChange,
    handleGridApiCampaignBlur,
    handleGridApiCampaignKeyDown,
  };
}
